import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { money } from "@/lib/api";
import { StatusBadge } from "@/components/shared";
import TrackingMap from "@/components/TrackingMap";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, Star, XCircle, Bike } from "lucide-react";

const TIMELINE = ["PENDING", "CONFIRMED", "PREPARING", "READY_FOR_PICKUP", "COURIER_ASSIGNED", "PICKED_UP", "IN_TRANSIT", "DELIVERED"];
const MAP_STATUSES = ["COURIER_ASSIGNED", "COURIER_ARRIVING", "PICKED_UP", "IN_TRANSIT", "DELIVERED"];
const CANCELLABLE = ["PENDING", "CONFIRMED", "PREPARING", "READY_FOR_PICKUP", "COURIER_ASSIGNED", "COURIER_ARRIVING"];
const TERMINAL = ["DELIVERED", "CANCELLED", "REJECTED"];

function EtaBanner({ track }) {
  const { t } = useTranslation();
  const eta = track.eta_minutes;
  let text;
  if (track.courier_stale) text = t("track.bannerStale");
  else if (!track.courier || eta == null) text = t("track.waiting");
  else if (eta <= 1) text = t("track.bannerArrivingNow");
  else if (track.phase === "to_customer") text = t("track.bannerArriving", { m: eta });
  else text = t("track.bannerPickup", { m: eta });
  const showKm = track.courier && !track.courier_stale && track.distance_remaining_km != null;
  return (
    <div data-testid="eta-banner" dir="auto" className="rounded-2xl bg-brand text-white px-5 py-4 flex items-center gap-4 shadow-lg">
      <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center shrink-0"><Bike className="w-5 h-5 animate-pulse" /></div>
      <div className="min-w-0">
        <div data-testid="eta-banner-text" className="text-lg font-display font-bold leading-tight">{text}</div>
        {showKm && <div className="text-xs text-white/80 mt-0.5">{t("track.kmAway", { km: track.distance_remaining_km.toFixed(1) })}</div>}
      </div>
    </div>
  );
}

export default function OrderTrack() {
  const { oid } = useParams();
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);
  const [track, setTrack] = useState(null);
  const [rating, setRating] = useState(0);

  const load = useCallback(async () => {
    const [o, tr] = await Promise.all([
      api.get(`/orders/${oid}`),
      api.get(`/orders/${oid}/tracking`).catch(() => ({ data: null })),
    ]);
    setOrder(o.data);
    setTrack(tr.data);
  }, [oid]);

  useEffect(() => { load(); }, [load]);

  // Poll live while the order is not terminal; stop automatically once delivered/cancelled.
  useEffect(() => {
    if (!order || TERMINAL.includes(order.status)) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [order?.status, load]);

  const rate = async () => {
    try { await api.post(`/orders/${oid}/rate`, { rating }); toast.success(t("track.thanks")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("track.rateFail")); }
  };

  const cancel = async () => {
    if (!window.confirm(t("track.cancelConfirm"))) return;
    try { await api.post(`/orders/${oid}/cancel`); toast.success(t("track.cancelled")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("track.cannotCancel")); }
  };

  if (!order) return <div className="h-96 rounded-2xl bg-muted animate-pulse" />;

  const reached = (s) => TIMELINE.indexOf(order.status) >= TIMELINE.indexOf(s) || order.status === "DELIVERED";
  const showMap = MAP_STATUSES.includes(order.status);
  const canCancel = CANCELLABLE.includes(order.status);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Link to="/app/orders" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> {t("track.orders")}</Link>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold">{order.store_name}</h1>
          <div className="text-sm text-muted-foreground font-mono">#{order.id.slice(-8)}</div>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={order.status} />
          {canCancel && <Button data-testid="cancel-order-btn" onClick={cancel} variant="outline" size="sm" className="rounded-full text-red-600 border-red-200 hover:bg-red-50 gap-1"><XCircle className="w-4 h-4" /> {t("track.cancel")}</Button>}
        </div>
      </div>

      {showMap && track && track.active && <EtaBanner track={track} />}
      {showMap && track && (
        <TrackingMap
          pickup={track.pickup}
          destination={track.destination}
          courier={track.courier}
          phase={track.phase}
          active={track.active}
          etaMinutes={track.eta_minutes}
          online={track.courier_online}
          stale={track.courier_stale}
          lastUpdated={track.last_updated}
        />
      )}
      {order.status === "CANCELLED" && <div data-testid="tracking-ended" className="rounded-2xl border bg-muted/40 p-4 text-sm text-muted-foreground">{t("track.ended")}</div>}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border bg-card p-5">
          <div className="font-semibold mb-4">{t("track.timeline")}</div>
          <div className="space-y-1">
            {TIMELINE.map((s, i) => (
              <div key={s} className="flex items-center gap-3">
                <div className="flex flex-col items-center">
                  {reached(s) ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-muted-foreground/40" />}
                  {i < TIMELINE.length - 1 && <div className={`w-0.5 h-6 ${reached(TIMELINE[i + 1]) ? "bg-emerald-500" : "bg-border"}`} />}
                </div>
                <span className={`text-sm ${reached(s) ? "font-medium" : "text-muted-foreground"}`}>{t(`status.${s}`, s.replace(/_/g, " ").toLowerCase())}</span>
              </div>
            ))}
          </div>
          {order.courier_name && <div className="mt-4 pt-4 border-t text-sm"><span className="text-muted-foreground">{t("track.courier")}:</span> <span className="font-medium">{order.courier_name}</span> · {order.recommended_vehicle}</div>}
        </div>

        <div className="rounded-2xl border bg-card p-5 space-y-3">
          <div className="font-semibold">{t("track.summary")}</div>
          <div className="space-y-2 max-h-48 overflow-auto">
            {order.items.map((it) => (
              <div key={it.product_id} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{it.quantity}× {it.name}</span><span>{money(it.price * it.quantity, order.currency)}</span>
              </div>
            ))}
          </div>
          <div className="h-px bg-border" />
          <div className="flex justify-between text-sm text-muted-foreground"><span>{t("track.subtotal")}</span><span>{money(order.subtotal, order.currency)}</span></div>
          <div className="flex justify-between text-sm text-muted-foreground"><span>{t("track.delivery")}</span><span>{money(order.delivery_fee, order.currency)}</span></div>
          <div className="flex justify-between text-sm text-muted-foreground"><span>{t("track.service")}</span><span>{money(order.service_fee, order.currency)}</span></div>
          {order.tax > 0 && <div className="flex justify-between text-sm text-muted-foreground"><span>{order.tax_name || "Tax"}{order.tax_inclusive ? " (incl.)" : ""}</span><span>{money(order.tax, order.currency)}</span></div>}
          <div className="flex justify-between font-semibold pt-1"><span>Total</span><span className="text-brand font-display text-lg">{money(order.total, order.currency)}</span></div>
          <div className="text-xs text-muted-foreground pt-1">{t("track.payment")}: {order.payment_status}</div>

          {order.status === "DELIVERED" && !order.rating && (
            <div className="pt-3 border-t">
              <div className="text-sm font-medium mb-2">{t("track.rate")}</div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)}><Star className={`w-6 h-6 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} /></button>
                ))}
              </div>
              <Button data-testid="submit-rating" onClick={rate} disabled={!rating} className="mt-3 rounded-full bg-brand hover:bg-brand-dark" size="sm">{t("track.submitRate")}</Button>
            </div>
          )}
          {order.rating && <div className="pt-3 border-t text-sm flex items-center gap-1"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {t("track.youRated", { n: order.rating })}</div>}
        </div>
      </div>
    </div>
  );
}
